import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "./AdminShared.css";

export default function AdminOffers() {
  const navigate = useNavigate();

  const [offers, setOffers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    badgeText: "",
    showPopup: true,
    showBadge: true,
    startDate: "",
    endDate: "",
    courses: [],
    image: null,
  });

  const stored = JSON.parse(localStorage.getItem("adminInfo") || "null");
  const token = stored?.token;

  /* ---------------- FETCH OFFERS ---------------- */

  const fetchOffers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setOffers(Array.isArray(data) ? data.reverse() : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  /* ---------------- FETCH COURSES ---------------- */

  const fetchCourses = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/courses`);
      const data = await res.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (!token) return navigate("/admin");
    fetchOffers();
    fetchCourses();
  }, [fetchOffers, fetchCourses, navigate, token]);

  /* ---------------- HANDLE INPUT ---------------- */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ---------------- SELECT COURSES ---------------- */

  const handleCourseSelect = (id) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.includes(id)
        ? prev.courses.filter((c) => c !== id)
        : [...prev.courses, id],
    }));
  };

  /* ---------------- IMAGE CHANGE ---------------- */

  const handleImageChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  /* ---------------- RESET FORM ---------------- */

  const resetForm = () => {
    setFormData({
      title: "", subtitle: "", badgeText: "",
      showPopup: true, showBadge: true,
      startDate: "", endDate: "",
      courses: [], image: null,
    });
  };

  /* ---------------- CREATE OFFER ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    form.append("title", formData.title);
    form.append("subtitle", formData.subtitle);
    form.append("badgeText", formData.badgeText);
    form.append("showPopup", formData.showPopup);
    form.append("showBadge", formData.showBadge);
    form.append("startDate", formData.startDate);
    form.append("endDate", formData.endDate);
    form.append("courses", JSON.stringify(formData.courses));
    if (formData.image) form.append("image", formData.image);

    try {
      const res = await fetch(`${API_BASE_URL}/api/offers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("Creation failed");
      alert("Campaign deployed successfully.");
      resetForm();
      setShowForm(false);
      fetchOffers();
    } catch (err) { alert(err.message); }
  };

  /* ---------------- TOGGLE OFFER ---------------- */

  const toggleOfferStatus = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/offers/${id}/toggle`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = await res.json();
      setOffers((prev) => prev.map((o) => (o._id === id ? updated : o)));
    } catch (err) { console.error(err); }
  };

  /* ---------------- DELETE OFFER ---------------- */

  const deleteOffer = async (id) => {
    if (!window.confirm("Purge this offer?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/offers/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchOffers();
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="admin-list-container"><p style={{padding: '100px', textAlign: 'center'}}>⏳ Synchronizing Campaign Data...</p></div>;

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Growth & Offers</h1>
        </div>
        <button className="module-badge" style={{ cursor: 'pointer', background: '#3b82f6', border: 'none' }} onClick={() => setShowForm(!showForm)}>
          {showForm ? "View Campaigns" : "+ Deploy New Campaign"}
        </button>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: '#3b82f6' }}></div>
            Context: Yield Optimization
          </div>
          <div className="admin-status-badge" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 800 }}>
             {offers.length} ACTIVE CAMPAIGNS
          </div>
        </div>

        {showForm ? (
            <div className="admin-table-wrapper" style={{ padding: '40px', background: '#fff' }}>
                <h2 style={{ marginBottom: '30px', fontWeight: 800 }}>Campaign Configuration</h2>
                <form className="admin-form-elite" style={{ padding: 0, border: 'none', boxShadow: 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }} onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <label>Campaign Title <input name="title" value={formData.title} onChange={handleChange} required /></label>
                        <label>Sub-text / Pitch <textarea name="subtitle" value={formData.subtitle} onChange={handleChange} required style={{ height: '100px' }} /></label>
                        <label>Badge Callout <input name="badgeText" value={formData.badgeText} onChange={handleChange} /></label>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <label style={{ flex: 1 }}>Activation Date <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required /></label>
                            <label style={{ flex: 1 }}>Expiry Node <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} required /></label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                         <div style={{ display: 'flex', gap: '30px', background: '#f8fafc', padding: '20px', borderRadius: '16px' }}>
                            <label style={{ margin: 0, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" name="showPopup" checked={formData.showPopup} onChange={handleChange} /> Deploy Popup
                            </label>
                            <label style={{ margin: 0, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" name="showBadge" checked={formData.showBadge} onChange={handleChange} /> Sticky Badge
                            </label>
                         </div>
                         <label>Target Audience (Courses)
                            <div className="course-grid" style={{ 
                                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', 
                                padding: '15px', background: '#f1f5f9', borderRadius: '16px', maxHeight: '150px', overflowY: 'auto'
                            }}>
                                {courses.map((course) => (
                                <label key={course._id} style={{ margin: 0, fontSize: '0.8rem', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                                    <input type="checkbox" checked={formData.courses.includes(course._id)} onChange={() => handleCourseSelect(course._id)} />
                                    {course.name}
                                </label>
                                ))}
                            </div>
                         </label>
                         <label>Visual Asset <input type="file" accept="image/*" onChange={handleImageChange} required /></label>
                         <button className="btn-primary-elite" type="submit" style={{ marginTop: 'auto' }}>Initialize Deployment</button>
                    </div>
                </form>
            </div>
        ) : (
            <div className="admin-gallery-grid">
                {offers.length === 0 ? (
                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '100px', color: '#94a3b8' }}>
                         No campaigns are currently in production.
                    </div>
                ) : (
                    offers.map((offer) => (
                        <div key={offer._id} className="admin-asset-card">
                            <div className="asset-preview">
                                <img src={`${API_BASE_URL}${offer.image}`} alt={offer.title} />
                                <div className="asset-tag" style={{ background: offer.isActive ? '#22c55e' : '#94a3b8' }}>
                                    {offer.isActive ? "ACTIVE" : "ARCHIVED"}
                                </div>
                            </div>
                            <div className="asset-info">
                                <h3 className="asset-name">{offer.title}</h3>
                                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '15px' }}>{offer.subtitle}</p>
                                <div className="asset-actions">
                                    <button className="action-btn-elite btn-update" onClick={() => toggleOfferStatus(offer._id)}>
                                        {offer.isActive ? "ARCHIVE" : "RE-ACTIVATE"}
                                    </button>
                                    <button className="action-btn-elite btn-delete" onClick={() => deleteOffer(offer._id)}>
                                        <span>🗑️</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        )}
      </div>
    </div>
  );
}
